import { useState, useMemo } from "react";
import {
  Container,
  Grid,
  Card,
  CardContent,
  Button,
  Typography,
  Box,
  Tab,
  IconButton,
  Tooltip,
  Tabs,
} from "@material-ui/core";
import themeColorsDesingSystem from "assets/theme/ds-colors";
import AddIcon from "@mui/icons-material/Add";
import CategoryIcon from "@mui/icons-material/Category";
import TuneIcon from "@mui/icons-material/Tune";
import RefreshIcon from "@mui/icons-material/Refresh";
import HeaderClientes from "components/_refactor/headers/clients";
import FilterCard from "components/_refactor/filter-card";
import InputText from "components/_refactor/text/input";
import AutoCompleteField from "components/_refactor/fields/autocomplete";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import IndicatorCards from "./components/indicator-cards";
import ContentTable from "./components/content-table";
import DialogNovoConteudo from "./components/dialog-novo-conteudo";
import DialogCampanha from "./components/dialog-campanha";
import DialogBanner from "./components/dialog-banner";
import DialogMaterial from "./components/dialog-material";
import DialogVideo from "./components/dialog-video";
import DialogCategorias from "./components/dialog-categorias";
import {
  CONTENT_TABS,
  CONTENT_TYPES,
  CONTENT_STATUS,
  SORT_OPTIONS,
  CAMPAIGN_CATEGORIES,
  MOCK_CONTENTS,
  matchesTab,
} from "./constants";

const DIALOG_MAP = {
  CAMPANHA: "campanha",
  BANNER: "banner",
  LOOKBOOK: "material",
  VIDEO: "video",
  MATERIAL: "material",
  CATEGORIAS: "categorias",
};

export default function GestaoMarketing() {
  const snackbar = useSnackbar();

  const [contents] = useState(MOCK_CONTENTS);
  const [activeTab, setActiveTab] = useState("TODOS");
  const [statusFilter, setStatusFilter] = useState(null);
  const [globalSearch, setGlobalSearch] = useState("");
  const [sortBy, setSortBy] = useState(SORT_OPTIONS[0]);
  const [filter, setFilter] = useState({
    search: "",
    tipo: null,
    categoria: null,
    status: null,
  });
  const [searching, setSearching] = useState(false);

  const [openNovoConteudo, setOpenNovoConteudo] = useState(false);
  const [activeDialog, setActiveDialog] = useState(null);
  const [contentToEdit, setContentToEdit] = useState(null);
  const [materialPreset, setMaterialPreset] = useState(null);

  const counts = useMemo(
    () => ({
      PUBLICADO: contents.filter((c) => c.status === "PUBLICADO").length,
      RASCUNHO: contents.filter((c) => c.status === "RASCUNHO").length,
      AGENDADO: contents.filter((c) => c.status === "AGENDADO").length,
      EXPIRADO: contents.filter((c) => c.status === "EXPIRADO").length,
    }),
    [contents],
  );

  const filteredData = useMemo(() => {
    let result = [...contents];

    result = result.filter((item) => matchesTab(item.tipo, activeTab));

    if (statusFilter) {
      result = result.filter((item) => item.status === statusFilter);
    }

    const searchTerm = (globalSearch || filter.search || "")
      .trim()
      .toLowerCase();
    if (searchTerm) {
      result = result.filter(
        (item) =>
          item.titulo.toLowerCase().includes(searchTerm) ||
          item.descricao.toLowerCase().includes(searchTerm) ||
          item.categoria.toLowerCase().includes(searchTerm),
      );
    }

    if (filter.tipo?.value) {
      result = result.filter((item) => item.tipo === filter.tipo.value);
    }

    if (filter.categoria?.label) {
      result = result.filter(
        (item) => item.categoria === filter.categoria.label,
      );
    }

    if (filter.status?.value) {
      result = result.filter((item) => item.status === filter.status.value);
    }

    if (sortBy?.value === "recentes") {
      result.sort(
        (a, b) => new Date(b.dataAtualizacao) - new Date(a.dataAtualizacao),
      );
    } else if (sortBy?.value === "atualizados") {
      result.sort(
        (a, b) => new Date(b.dataAtualizacao) - new Date(a.dataAtualizacao),
      );
    } else if (sortBy?.value === "acessados") {
      result.sort((a, b) => b.acessos - a.acessos);
    }

    return result;
  }, [contents, activeTab, statusFilter, globalSearch, filter, sortBy]);

  const handleSearch = () => {
    setSearching(true);
    setTimeout(() => setSearching(false), 300);
  };

  const handleClearFilters = () => {
    setFilter({ search: "", tipo: null, categoria: null, status: null });
    setStatusFilter(null);
    setGlobalSearch("");
  };

  const openDialog = (type, content = null, preset = null) => {
    setContentToEdit(content);
    setMaterialPreset(preset);
    setActiveDialog(type);
    setOpenNovoConteudo(false);
  };

  const closeDialog = () => {
    setActiveDialog(null);
    setContentToEdit(null);
    setMaterialPreset(null);
  };

  const handleNovoConteudoSelect = (type) => {
    const dialogType = DIALOG_MAP[type];
    if (type === "LOOKBOOK") {
      openDialog("material", null, "LOOKBOOK");
    } else if (dialogType) {
      openDialog(dialogType);
    }
  };

  const handleTableAction = (action, row) => {
    const messages = {
      visualizar: `Visualizando "${row.titulo}"`,
      duplicar: `Cópia de "${row.titulo}" criada`,
      publicar: `"${row.titulo}" publicado`,
      despublicar: `"${row.titulo}" despublicado`,
      arquivar: `"${row.titulo}" arquivado`,
      excluir: `"${row.titulo}" excluído`,
      compartilhar: `Link de "${row.titulo}" copiado`,
      historico: `Histórico de "${row.titulo}"`,
    };

    if (action === "editar") {
      const dialogType = DIALOG_MAP[row.tipo] || DIALOG_MAP.MATERIAL;
      const preset = row.tipo === "LOOKBOOK" ? "LOOKBOOK" : null;
      openDialog(
        dialogType === "material" && row.tipo === "LOOKBOOK"
          ? "material"
          : dialogType,
        row,
        preset,
      );
      return;
    }

    snackbar.showMessage(messages[action] || "Ação executada", {
      severity: action === "excluir" ? "warning" : "success",
    });
  };

  return (
    <>
      <HeaderClientes
        section="Gestão de Marketing"
        subsection="Marketing"
        tooltipMessage="Gerencie campanhas, banners, materiais, lookbooks e vídeos para as franquias."
      />
      <Container maxWidth={false} style={{ marginTop: "-4.5rem" }}>
        <Card style={{ marginBottom: 16, borderRadius: 12 }}>
          <CardContent>
            <Typography
              variant="body2"
              style={{
                color: themeColorsDesingSystem.gray.dark,
                marginBottom: 16,
              }}
            >
              Centralize e publique conteúdos oficiais da marca para as
              franquias.
            </Typography>
            <Grid container spacing={2} alignItems="flex-end">
              <Grid item xs={12} sm={6} md={4}>
                <InputText
                  label="Buscar conteúdos..."
                  value={globalSearch}
                  onChange={(e) => setGlobalSearch(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <AutoCompleteField
                  label="Ordenação"
                  value={sortBy}
                  onChange={setSortBy}
                  options={SORT_OPTIONS}
                  optionLabelKey="label"
                  optionIdKey="value"
                />
              </Grid>
              <Grid item xs={12} md={5}>
                <Box
                  display="flex"
                  flexDirection="row"
                  flexWrap="wrap"
                  justifyContent="flex-end"
                  alignItems="center"
                  style={{ gap: 8 }}
                >
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<CategoryIcon />}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                    onClick={() => openDialog("categorias")}
                  >
                    Categorias
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                    onClick={() => setOpenNovoConteudo(true)}
                  >
                    Novo Conteúdo
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Box marginBottom={3}>
          <IndicatorCards
            counts={counts}
            activeStatus={statusFilter}
            onStatusClick={setStatusFilter}
          />
        </Box>

        <Card
          style={{ marginBottom: 16, borderRadius: 12, overflow: "hidden" }}
        >
          <Tabs
            value={activeTab}
            onChange={(_, value) => setActiveTab(value)}
            variant="scrollable"
            scrollButtons="auto"
            indicatorColor="primary"
            textColor="primary"
            style={{ backgroundColor: themeColorsDesingSystem.background.main }}
          >
            {CONTENT_TABS.map((tab) => (
              <Tab
                key={tab.key}
                label={tab.label}
                value={tab.key}
                style={{
                  textTransform: "none",
                  fontWeight: activeTab === tab.key ? 600 : 400,
                  color: themeColorsDesingSystem.text.primary,
                  minWidth: 100,
                }}
              />
            ))}
          </Tabs>
        </Card>

        <FilterCard
          handleSearch={handleSearch}
          handleClearFilters={handleClearFilters}
          searching={searching}
          disabled={searching}
          title="Filtros"
        >
          <Grid item xs={12} lg={4}>
            <InputText
              label="Buscar por título"
              value={filter.search}
              onChange={(e) => setFilter({ ...filter, search: e.target.value })}
              disabled={searching}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            />
          </Grid>
          <Grid item xs={12} lg={3}>
            <AutoCompleteField
              label="Tipo"
              value={filter.tipo}
              onChange={(v) => setFilter({ ...filter, tipo: v })}
              options={CONTENT_TYPES}
              optionLabelKey="label"
              optionIdKey="value"
              disabled={searching}
            />
          </Grid>
          <Grid item xs={12} lg={3}>
            <AutoCompleteField
              label="Categoria"
              value={filter.categoria}
              onChange={(v) => setFilter({ ...filter, categoria: v })}
              options={CAMPAIGN_CATEGORIES}
              optionLabelKey="label"
              optionIdKey="value"
              disabled={searching}
            />
          </Grid>
          <Grid item xs={12} lg={2}>
            <AutoCompleteField
              label="Status"
              value={filter.status}
              onChange={(v) => setFilter({ ...filter, status: v })}
              options={CONTENT_STATUS}
              optionLabelKey="label"
              optionIdKey="value"
              disabled={searching}
            />
          </Grid>
        </FilterCard>

        <Card style={{ borderRadius: 12, marginTop: 16 }}>
          <CardContent>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              marginBottom={2}
              flexDirection="row"
            >
              <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                <Typography
                  variant="h3"
                  style={{ color: themeColorsDesingSystem.text.primary }}
                >
                  Conteúdos
                </Typography>
                <Tooltip title="Atualizar">
                  <IconButton
                    size="small"
                    onClick={handleSearch}
                    aria-label="Atualizar"
                  >
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Typography
                  variant="caption"
                  style={{ color: themeColorsDesingSystem.gray.dark }}
                >
                  {filteredData.length} resultado(s)
                </Typography>
              </Box>
              <Button
                variant="text"
                color="primary"
                startIcon={<TuneIcon />}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                onClick={handleClearFilters}
                size="small"
              >
                Limpar filtros
              </Button>
            </Box>
            <ContentTable
              data={filteredData}
              loading={searching}
              onAction={handleTableAction}
            />
          </CardContent>
        </Card>
      </Container>

      <DialogNovoConteudo
        open={openNovoConteudo}
        onClose={() => setOpenNovoConteudo(false)}
        onSelect={handleNovoConteudoSelect}
      />
      <DialogCampanha
        open={activeDialog === "campanha"}
        onClose={closeDialog}
        content={contentToEdit}
      />
      <DialogBanner
        open={activeDialog === "banner"}
        onClose={closeDialog}
        content={contentToEdit}
      />
      <DialogMaterial
        open={activeDialog === "material"}
        onClose={closeDialog}
        content={contentToEdit}
        presetTipo={materialPreset}
      />
      <DialogVideo
        open={activeDialog === "video"}
        onClose={closeDialog}
        content={contentToEdit}
      />
      <DialogCategorias
        open={activeDialog === "categorias"}
        onClose={closeDialog}
      />
    </>
  );
}
